import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from './ui/sidebar';
import { 
  Car, 
  Settings,
  History,
  HelpCircle,
  Package,
  DollarSign,
  BarChart3,
  Coins,
  FileSignature,
  Plus,
  Users,
  LogOut
} from 'lucide-react';

interface AppSidebarProps {
  currentPage: string;
}

export default function AppSidebar({ currentPage }: AppSidebarProps) {
  const { t } = useLanguage();
  const { state } = useSidebar();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isCollapsed = state === 'collapsed';

  const sellingMenuItems = [
    {
      id: 'warehouse',
      label: t('nav.warehouse'),
      icon: Package,
      path: '/warehouse'
    },
    {
      id: 'sell-car',
      label: t('nav.sellCar'),
      icon: Car,
      path: '/sell-car'
    },
    {
      id: 'sell-car-history',
      label: t('nav.sellCarHistory'),
      icon: History,
      path: '/sell-car-history'
    },
    {
      id: 'expenses',
      label: t('nav.expenses'),
      icon: DollarSign,
      path: '/expenses'
    },
    {
      id: 'report',
      label: t('nav.report'),
      icon: BarChart3,
      path: '/report'
    }
  ];

  const agreementsMenuItems = [
    {
      id: 'agreements',
      label: t('nav.agreements'),
      icon: FileSignature,
      path: '/agreements'
    },
    {
      id: 'agreements/add',
      label: t('nav.addAgreement'),
      icon: Plus,
      path: '/agreements/add'
    }
  ];

  const exchangeMenuItems = [
    {
      id: 'exchange-cars',
      label: t('nav.exchangeCarHistory'),
      icon: History,
      path: '/exchange-cars'
    },
    {
      id: 'exchange-cars/add',
      label: t('nav.exchangeCar'),
      icon: Plus,
      path: '/exchange-cars/add'
    }
  ];

  const currenciesMenuItems = [
    {
      id: 'currency-exchange',
      label: t('nav.currencyExchange'),
      icon: Coins,
      path: '/currency-exchange'
    }
  ];

  const carsOfDaraMenuItems = [
    {
      id: 'cars-of-dara',
      label: t('nav.addCarOfDara'),
      icon: Plus,
      path: '/cars-of-dara'
    },
    {
      id: 'cars-of-dara-list',
      label: t('nav.carsOfDaraList'),
      icon: Car,
      path: '/cars-of-dara/list'
    }
  ];

  const bottomMenuItems = [
    {
      id: 'help',
      label: t('nav.help'),
      icon: HelpCircle,
      path: '/help'
    },
    ...(user?.role === 'ADMIN' ? [{
      id: 'users',
      label: t('nav.users'),
      icon: Users,
      path: '/users'
    }] : []),
    {
      id: 'settings',
      label: t('nav.settings'),
      icon: Settings,
      path: '/settings'
    }
  ];

  return (
    <Sidebar 
      collapsible="icon"
      className="border-r border-gray-200 bg-white/80 backdrop-blur-sm"
    >
      <div className="flex h-full flex-col">
        {/* Header */}
        <div className="flex items-center p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-cyan-400 to-emerald-400 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">S</span>
            </div>
            {!isCollapsed && (
              <div>
                <h1 className="font-bold text-gray-800">The S Car POS</h1>
                <p className="text-xs text-gray-500">Point of Sale</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <SidebarContent className="flex-1">
          {/* Selling Group */}
          <SidebarGroup>
            <SidebarGroupLabel className="text-gray-600 font-medium">
              {!isCollapsed && t('nav.selling')}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {sellingMenuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        asChild
                        className={`w-full justify-start ${
                          currentPage === item.id
                            ? 'bg-gradient-to-r from-cyan-50 to-emerald-50 text-cyan-700 border-r-2 border-cyan-500'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <Link to={item.path}>
                          <Icon className="h-4 w-4" />
                          {!isCollapsed && (
                            <span className="font-medium">{item.label}</span>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Agreements Group */}
          <SidebarGroup>
            <SidebarGroupLabel className="text-gray-600 font-medium">
              {!isCollapsed && t('nav.agreementsGroup')}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {agreementsMenuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        asChild
                        className={`w-full justify-start ${
                          currentPage === item.id
                            ? 'bg-gradient-to-r from-cyan-50 to-emerald-50 text-cyan-700 border-r-2 border-cyan-500'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <Link to={item.path}>
                          <Icon className="h-4 w-4" />
                          {!isCollapsed && (
                            <span className="font-medium">{item.label}</span>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Exchange Group */}
          <SidebarGroup>
            <SidebarGroupLabel className="text-gray-600 font-medium">
              {!isCollapsed && t('nav.exchange')}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {exchangeMenuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        asChild
                        className={`w-full justify-start ${
                          currentPage === item.id
                            ? 'bg-gradient-to-r from-cyan-50 to-emerald-50 text-cyan-700 border-r-2 border-cyan-500'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <Link to={item.path}>
                          <Icon className="h-4 w-4" />
                          {!isCollapsed && (
                            <span className="font-medium">{item.label}</span>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Cars of Dara Group */}
          <SidebarGroup>
            <SidebarGroupLabel className="text-gray-600 font-medium">
              {!isCollapsed && t('nav.carsOfDaraGroup')}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {carsOfDaraMenuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        asChild
                        className={`w-full justify-start ${
                          currentPage === item.id
                            ? 'bg-gradient-to-r from-cyan-50 to-emerald-50 text-cyan-700 border-r-2 border-cyan-500'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <Link to={item.path}>
                          <Icon className="h-4 w-4" />
                          {!isCollapsed && (
                            <span className="font-medium">{item.label}</span>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Currencies Group */}
          <SidebarGroup>
            <SidebarGroupLabel className="text-gray-600 font-medium">
              {!isCollapsed && t('nav.currenciesGroup')}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {currenciesMenuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        asChild
                        className={`w-full justify-start ${
                          currentPage === item.id
                            ? 'bg-gradient-to-r from-cyan-50 to-emerald-50 text-cyan-700 border-r-2 border-cyan-500'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <Link to={item.path}>
                          <Icon className="h-4 w-4" />
                          {!isCollapsed && (
                            <span className="font-medium">{item.label}</span>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        {/* Bottom Menu - Help and Settings */}
        <div className="mt-auto">
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  {bottomMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <SidebarMenuItem key={item.id}>
                        <SidebarMenuButton
                          asChild
                          className={`w-full justify-start ${
                            currentPage === item.id
                              ? 'bg-gradient-to-r from-cyan-50 to-emerald-50 text-cyan-700 border-r-2 border-cyan-500'
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <Link to={item.path}>
                            <Icon className="h-4 w-4" />
                            {!isCollapsed && (
                              <span className="font-medium">{item.label}</span>
                            )}
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      className="w-full justify-start hover:bg-red-50 text-red-600 hover:text-red-700"
                      onClick={() => {
                        logout();
                        navigate('/login');
                      }}
                    >
                      <LogOut className="h-4 w-4" />
                      {!isCollapsed && (
                        <span className="font-medium">Logout</span>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          {!isCollapsed && (
            <div className="text-xs text-gray-500 text-center">
              © 2025 The S Car POS
            </div>
          )}
        </div>
      </div>
    </Sidebar>
  );
}
